import type {
  FactoriesFactoryLine,
  FactoriesWorkOrder,
  FactoriesWorkOrderExecution,
  FactoriesWorkOrderLineDispatch,
} from "@/api-client";
import { automationNameForLineStep, lineStepParallelism } from "./factoryLineFormShared";
import { factoryAppPath, factoryAppRunPath, factoryHomePath, factoryLineDetailPath } from "./factoryPagePaths";
import { getWorkOrderDisplayStatus } from "./workOrderProgress";
import { dispatchStepRows, isActiveWorkOrderExecution, type WorkOrderStepRow } from "./workOrderExecutions";
import { resolvePhaseRunStatus } from "./linePhaseRunStatus";

export { resolvePhaseRunStatus } from "./linePhaseRunStatus";
export type LinePhaseTick = "running" | "waiting" | "queued" | "failed" | null;

/** Phase status expressed with a distinct glyph shape, not colour alone. */
export type PhaseGlyphKind = "running" | "waiting" | "queued" | "failed" | "passed" | "pending" | "cancelled";

export type LinePhaseRunCard = {
  executionId: string;
  workOrderId: string;
  /** Raw task, so the board can build the shared task card model. */
  order: FactoriesWorkOrder;
  execution: WorkOrderStepRow;
};

export type LinePhaseColumn = {
  stepName: string;
  stepIndex: number;
  /** Factory app id for this runApp step, when present. */
  appId?: string;
  /** In-flight cap for this step. Defaults to 10 when the line omits it. */
  maxParallelism: number;
  runs: LinePhaseRunCard[];
  tick: LinePhaseTick;
};

/** Initial / step size for the scrollable phase-column run list. */
export const LINE_PHASE_RUNS_PAGE_SIZE = 3;

/**
 * Destination for a phase-board card: the split-run page for this phase.
 * Never the task page — that destination stays on the Tasks list.
 */
export function linePhaseRunHref(
  organizationId: string,
  factoryKey: string,
  lineId: string | undefined,
  run: LinePhaseRunCard,
  stepAppId?: string,
): string {
  const appId = run.execution.run?.appId || stepAppId;
  const runId = run.execution.run?.id;
  if (appId && runId) {
    return factoryAppRunPath(organizationId, factoryKey, appId, runId, {
      from: "lines",
      lineId,
      orderNumber: run.order.number,
    });
  }
  if (appId) {
    return factoryAppPath(organizationId, factoryKey, appId, { from: "lines", lineId });
  }
  if (lineId) {
    return factoryLineDetailPath(organizationId, factoryKey, lineId);
  }
  return factoryHomePath(organizationId, factoryKey);
}

/**
 * Builds the Lines detail board: one column per line step. Each task
 * appears once, in the column for its current (furthest active, else furthest
 * finished) step on this line — newest cards first within a column. Work
 * orders that reached a terminal outcome move to the Done column instead.
 */
export function buildLinePhaseBoard(
  line: FactoriesFactoryLine,
  workOrders: FactoriesWorkOrder[],
  apps: Array<{ id?: string; name?: string }> = [],
): LinePhaseColumn[] {
  const lineId = line.id;
  const steps = line.steps ?? [];
  if (!lineId || steps.length === 0) {
    return [];
  }

  const columns: LinePhaseColumn[] = steps.map((step, stepIndex) => ({
    stepName: automationNameForLineStep(step, apps, stepIndex),
    stepIndex,
    appId: step.app?.app?.trim() || undefined,
    maxParallelism: lineStepParallelism(step),
    runs: [],
    tick: null,
  }));

  const runsByStep = collectCurrentRunsByStep(lineId, steps, workOrders);
  for (const column of columns) {
    column.runs = runsByStep.get(column.stepIndex) ?? [];
    column.tick = resolvePhaseTick(column.runs);
  }

  return columns;
}

/**
 * True when the last step is the line's own Done automation. Such a line keeps
 * finished tasks on that step; every other line hands them to the board
 * Done column, which no app backs.
 */
export function lineBoardEndsWithDoneStep(columns: LinePhaseColumn[]): boolean {
  const last = columns[columns.length - 1];
  return last ? isDoneLineColumn(last) : false;
}

/**
 * Draft tasks. A draft that already ran on a line still belongs
 * here after To Backlog. Newest updated drafts come first.
 */
export function collectLineBacklogOrders(workOrders: FactoriesWorkOrder[]): FactoriesWorkOrder[] {
  return workOrders.filter(isLineBacklogOrder).sort(compareOrdersNewestFirst);
}

/**
 * Closed work that belongs on this line, plus open work still on a Done or
 * PR-closure step. Newest orders come first. A draft rejected straight out
 * of the Backlog never dispatched to a line, so it is excluded — it left
 * the board when it closed, and Done is not the right home for work that
 * never started. See isArchivedBacklogReject.
 */
export function collectLineDoneOrders(
  workOrders: FactoriesWorkOrder[],
  line: FactoriesFactoryLine,
  board: LinePhaseColumn[] = [],
): FactoriesWorkOrder[] {
  const doneById = new Map<string, FactoriesWorkOrder>();

  for (const order of workOrders) {
    if (!order.id || order.state !== "STATE_CLOSED" || !belongsToLineBoard(order, line.id)) {
      continue;
    }
    if (isArchivedBacklogReject(order)) {
      continue;
    }
    doneById.set(order.id, order);
  }

  for (const column of board) {
    if (!isDoneLineColumn(column)) {
      continue;
    }
    for (const run of column.runs) {
      if (run.order.id) {
        doneById.set(run.order.id, run.order);
      }
    }
  }

  return [...doneById.values()].sort(compareOrdersNewestFirst);
}

/** Open work that waits for review after the last stage passed. Newest first. */
export function collectLineVerifyOrders(board: LinePhaseColumn[]): FactoriesWorkOrder[] {
  const lastStage = lineStageColumns(board).at(-1);
  if (!lastStage) {
    return [];
  }

  const verifyById = new Map<string, FactoriesWorkOrder>();
  for (const run of lastStage.runs) {
    if (!run.order.id || !isWaitingAfterPassedStage(run)) {
      continue;
    }
    verifyById.set(run.order.id, run.order);
  }

  return [...verifyById.values()].sort(compareOrdersNewestFirst);
}

function isWaitingAfterPassedStage(run: LinePhaseRunCard): boolean {
  if (run.order.state === "STATE_CLOSED") {
    return false;
  }
  if (getWorkOrderDisplayStatus(run.order) !== "waiting") {
    return false;
  }
  return run.execution.state === "STATE_FINISHED" && run.execution.result === "RESULT_PASSED";
}

/** Stage columns only. Done is a fixed bookend, not a line step. */
export function lineStageColumns(columns: LinePhaseColumn[]): LinePhaseColumn[] {
  return columns.filter((column) => !isDoneLineColumn(column));
}

/** Stage columns with waiting review work already moved to Verify. */
export function visibleLineStageColumns(
  columns: LinePhaseColumn[],
  verifyOrders: FactoriesWorkOrder[],
): LinePhaseColumn[] {
  const verifyIds = new Set(verifyOrders.flatMap((order) => (order.id ? [order.id] : [])));
  return lineStageColumns(columns).map((column) => {
    const runs = column.runs.filter((run) => !verifyIds.has(run.workOrderId));
    if (runs.length === column.runs.length) {
      return column;
    }
    return { ...column, runs, tick: resolvePhaseTick(runs) };
  });
}

/** Factory-level intake automation. It is not a line step. */
export function findBacklogAutomationApp(
  apps: Array<{ id?: string; name?: string }>,
): { id: string; name: string } | undefined {
  const match = apps.find(
    (app) => app.id && (app.name === "Backlog" || app.name === "Ingest" || app.id === "app-refund-backlog"),
  );
  if (!match?.id) {
    return undefined;
  }
  return { id: match.id, name: match.name ?? "Ingest" };
}

/** Factory-level PR Closure automation. It is not a line step. */
export function findClosureAutomationApp(
  apps: Array<{ id?: string; name?: string }>,
): { id: string; name: string } | undefined {
  const match = apps.find(
    (app) =>
      Boolean(app.id) &&
      (app.name === "PR Closure" || app.id === "app-refund-done" || (app.id ?? "").includes("pr-closure")),
  );
  if (!match?.id) {
    return undefined;
  }
  return { id: match.id, name: match.name ?? "PR Closure" };
}

/** Backlog and Done are not canvas-backed columns. */
export function isDoneLineColumn(column: Pick<LinePhaseColumn, "stepName" | "appId">): boolean {
  if (column.stepName.trim().toLowerCase() === "done") {
    return true;
  }
  if (!column.appId) {
    return false;
  }
  return column.appId === "app-refund-done" || column.appId.includes("pr-closure");
}

function isLineBacklogOrder(order: FactoriesWorkOrder): boolean {
  return Boolean(order.id) && order.state === "STATE_DRAFT";
}

// A rejected order that never dispatched to any line was rejected straight
// out of the Backlog — it never ran a single step. Reject on a scored or
// imported draft the operator will not start should archive it off the
// board, not park it in Done next to work that actually finished. A
// rejected order that did dispatch (it started, then was later rejected)
// keeps its place in Done; that decision is unaffected here.
function isArchivedBacklogReject(order: FactoriesWorkOrder): boolean {
  return order.result === "RESULT_REJECTED" && (order.lineDispatches ?? []).length === 0;
}

// A finished task belongs on this board when it ran on this line, or
// when it closed before any line picked it up — the same rule the shared
// backlog follows.
function belongsToLineBoard(order: FactoriesWorkOrder, lineId: string | undefined): boolean {
  const dispatches = order.lineDispatches ?? [];
  if (dispatches.length === 0) {
    return true;
  }
  return dispatches.some((dispatch) => dispatch.line?.id === lineId);
}

function compareOrdersNewestFirst(left: FactoriesWorkOrder, right: FactoriesWorkOrder): number {
  const leftTime = Date.parse(left.updatedAt ?? left.createdAt ?? "") || 0;
  const rightTime = Date.parse(right.updatedAt ?? right.createdAt ?? "") || 0;
  if (leftTime !== rightTime) {
    return rightTime - leftTime;
  }
  return (right.id ?? "").localeCompare(left.id ?? "");
}

/** Board-level status for a phase column header. */
export function resolveColumnGlyph(column: LinePhaseColumn): PhaseGlyphKind {
  if (column.tick) {
    return column.tick;
  }
  return column.runs.length > 0 ? "passed" : "pending";
}

/** Row-level status for a single run card. */
export function resolveRunGlyph(run: LinePhaseRunCard): PhaseGlyphKind {
  const { kind, label } = resolvePhaseRunStatus(run.execution);
  if (kind === "idle") {
    return label === "Passed" ? "passed" : "pending";
  }
  return kind;
}

function collectCurrentRunsByStep(
  lineId: string,
  steps: NonNullable<FactoriesFactoryLine["steps"]>,
  workOrders: FactoriesWorkOrder[],
): Map<number, LinePhaseRunCard[]> {
  const runsByStep = new Map<number, LinePhaseRunCard[]>();
  for (const order of workOrders) {
    appendCurrentRunForOrder(order, lineId, steps, runsByStep);
  }
  for (const runs of runsByStep.values()) {
    runs.sort(compareRunsNewestFirst);
  }
  return runsByStep;
}

function executionStepIndex(execution: FactoriesWorkOrderExecution): number | undefined {
  if (execution.stepIndex == null || execution.stepIndex < 0) {
    return undefined;
  }
  return execution.stepIndex;
}

function liveColumnIndexForExecution(
  steps: NonNullable<FactoriesFactoryLine["steps"]>,
  execution: FactoriesWorkOrderExecution,
): number | undefined {
  const stepIndex = executionStepIndex(execution);
  if (stepIndex == null) {
    return undefined;
  }

  // Dispatch stepIndex is a snapshot. A later line edit can move that
  // index onto a different automation. Keep the snapshot index when the
  // app still matches; otherwise place the card on the live column with
  // the same app.
  const executionAppId = execution.run?.appId?.trim();
  const liveAppId = steps[stepIndex]?.app?.app?.trim();
  if (stepIndex < steps.length && (!executionAppId || liveAppId === executionAppId)) {
    return stepIndex;
  }

  if (!executionAppId) {
    return undefined;
  }

  return closestAppColumnIndex(steps, executionAppId, stepIndex);
}

function closestAppColumnIndex(
  steps: NonNullable<FactoriesFactoryLine["steps"]>,
  executionAppId: string,
  stepIndex: number,
): number | undefined {
  const matches: number[] = [];
  for (let index = 0; index < steps.length; index++) {
    if (steps[index]?.app?.app?.trim() === executionAppId) {
      matches.push(index);
    }
  }
  if (matches.length === 0) {
    return undefined;
  }
  if (matches.length === 1) {
    return matches[0];
  }
  return matches.reduce((best, index) => (Math.abs(index - stepIndex) < Math.abs(best - stepIndex) ? index : best));
}

function appendCurrentRunForOrder(
  order: FactoriesWorkOrder,
  lineId: string,
  steps: NonNullable<FactoriesFactoryLine["steps"]>,
  runsByStep: Map<number, LinePhaseRunCard[]>,
): void {
  if (!order.id || order.state === "STATE_CLOSED" || order.state === "STATE_DRAFT") {
    return;
  }

  // A line can have more than one dispatch (traversal) for this order; the
  // board is a compact status summary, so it shows the most recent one —
  // the full history of every dispatch lives in WorkOrderExecutionsList.
  const dispatchesForLine = (order.lineDispatches ?? []).filter((dispatch) => dispatch.line?.id === lineId);
  if (dispatchesForLine.length === 0) {
    return;
  }
  const currentDispatch = pickMostRecentDispatch(dispatchesForLine);

  const lineExecutions = dispatchStepRows(currentDispatch).filter(
    (execution) => liveColumnIndexForExecution(steps, execution) != null,
  );
  if (lineExecutions.length === 0) {
    return;
  }

  const currentExecution = pickCurrentLineExecution(lineExecutions);
  const stepIndex = currentExecution ? liveColumnIndexForExecution(steps, currentExecution) : undefined;
  if (!currentExecution || stepIndex == null) {
    return;
  }

  const card: LinePhaseRunCard = {
    executionId: currentExecution.id ?? `${order.id}-${stepIndex}-${currentExecution.createdAt ?? ""}`,
    workOrderId: order.id,
    order,
    execution: currentExecution,
  };
  const existing = runsByStep.get(stepIndex);
  if (existing) {
    existing.push(card);
    return;
  }
  runsByStep.set(stepIndex, [card]);
}

function pickMostRecentDispatch(dispatches: FactoriesWorkOrderLineDispatch[]): FactoriesWorkOrderLineDispatch {
  return dispatches.reduce((latest, candidate) => {
    const latestAt = Date.parse(latest.createdAt ?? "") || 0;
    const candidateAt = Date.parse(candidate.createdAt ?? "") || 0;
    return candidateAt >= latestAt ? candidate : latest;
  });
}

function executionTimestamp(execution: WorkOrderStepRow): number {
  return Date.parse(execution.updatedAt ?? execution.createdAt ?? "") || 0;
}

function isPreferableCurrentExecution(candidate: WorkOrderStepRow, incumbent: WorkOrderStepRow): boolean {
  const candidateStep = executionStepIndex(candidate) ?? -1;
  const incumbentStep = executionStepIndex(incumbent) ?? -1;
  if (candidateStep !== incumbentStep) {
    return candidateStep > incumbentStep;
  }
  const candidateTime = executionTimestamp(candidate);
  const incumbentTime = executionTimestamp(incumbent);
  if (candidateTime !== incumbentTime) {
    return candidateTime > incumbentTime;
  }
  return (candidate.id ?? "") > (incumbent.id ?? "");
}

function pickCurrentLineExecution(executions: WorkOrderStepRow[]): WorkOrderStepRow | null {
  const active = executions.filter(isActiveWorkOrderExecution);
  const candidates = active.length > 0 ? active : executions;
  let best: WorkOrderStepRow | null = null;

  for (const execution of candidates) {
    if (executionStepIndex(execution) == null) {
      continue;
    }
    if (!best || isPreferableCurrentExecution(execution, best)) {
      best = execution;
    }
  }

  return best;
}

function compareRunsNewestFirst(left: LinePhaseRunCard, right: LinePhaseRunCard): number {
  const leftTime = Date.parse(left.execution.updatedAt ?? left.execution.createdAt ?? "") || 0;
  const rightTime = Date.parse(right.execution.updatedAt ?? right.execution.createdAt ?? "") || 0;
  if (leftTime !== rightTime) {
    return rightTime - leftTime;
  }
  return (right.executionId ?? "").localeCompare(left.executionId ?? "");
}

function resolvePhaseTick(runs: LinePhaseRunCard[]): LinePhaseTick {
  let hasRunning = false;
  let hasWaiting = false;
  let hasQueued = false;

  for (const run of runs) {
    const { kind } = resolvePhaseRunStatus(run.execution);
    // Finished failed runs are row-level only — do not drive the phase aggregate.
    if (kind === "running") hasRunning = true;
    else if (kind === "waiting") hasWaiting = true;
    else if (kind === "queued") hasQueued = true;
    else if (isActiveWorkOrderExecution(run.execution)) hasQueued = true;
  }

  if (hasWaiting) return "waiting";
  if (hasRunning) return "running";
  if (hasQueued) return "queued";
  return null;
}
